import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Tooltip } from "antd"
import "./BorrowerLoanInfoV2.css"
import { getLosApplicationV2 } from "../DocumentChecklist/applicationV2Client"
import {
    formatAddress,
    formatLoanAmount,
    formatLtv,
    formatPurpose,
    formatRate,
    getBorrowerHeaderDisplay,
    getDisplayRateNumber,
} from "./formatters"
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'

const coborrowerBadgeStyle = {
    display: "inline-block",
    marginLeft: 6,
    padding: "1px 8px",
    borderRadius: 10,
    background: "#EEF2FF",
    color: "#325CEB",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "18px",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
}

const linkStyle = {
    color: "#325CEB",
    fontWeight: 500,
    cursor: "pointer",
    marginRight: 4,
}

export default function BorrowerLoanInfoV2({
    loanId,
    borrowerId,
    application: applicationProp,
    purposeOverride,
    rateOverride,
    onRateClick,
    dtiLabel,
    onDTIClick,
}) {
    const [fetched, setFetched] = useState(null)

    useEffect(() => {
        setFetched(null)
    }, [loanId])

    useEffect(() => {
        if (!loanId || applicationProp) return undefined
        let cancelled = false
        getLosApplicationV2(loanId)
            .then((body) => {
                if (!cancelled) setFetched(body.Application || null)
            })
            .catch((err) => {
                console.error(err)
            })
        return () => {
            cancelled = true
        }
    }, [loanId, applicationProp])

    const application = applicationProp || fetched
    const purposeLabel = !application
        ? "-"
        : (purposeOverride || formatPurpose(application?.property?.purpose))
    const borrowerHref = borrowerId ? `/app/borrowers/${borrowerId}` : ""
    const header = getBorrowerHeaderDisplay(application)
    const fromAppRate = application ? getDisplayRateNumber(application) : 0
    const overrideRate = Number(rateOverride)
    const rateNumber = Number.isFinite(overrideRate) && overrideRate !== 0
        ? overrideRate
        : fromAppRate
    const rateLabel = rateNumber ? `${rateNumber}%` : (application ? formatRate(application) : "-")

    const borrowerValue = (
        <span className="borrowerLoanInfoV2-borrower">
            <span className="borrowerLoanInfoV2-borrower-name">{header.primaryName}</span>
            {header.coCount > 0 && (
                <span style={coborrowerBadgeStyle}>
                    +{header.coCount} coborrower{header.coCount > 1 ? "s" : ""}
                </span>
            )}
        </span>
    )

    // Always a tooltip: the name can be cut off when the bar is narrow.
    const borrowerValueWithTip = (
        <Tooltip title={header.fullLabel} placement="bottomLeft">
            {borrowerValue}
        </Tooltip>
    )

    const linkValue = (label, onClick) => (onClick ? (
        <span
            role="button"
            tabIndex={0}
            style={linkStyle}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onClick()
                }
            }}
        >
            {label}
        </span>
    ) : label)

    const rateValue = linkValue(rateLabel, onRateClick)
    // Until the V2 application has loaded there is nothing to calculate from.
    const dtiValue = onDTIClick ? linkValue(dtiLabel || "Calculate", onDTIClick) : "-"
    const address = formatAddress(application?.property)

    return (
        <div className="borrowerLoanInfo borrowerLoanInfoV2">
            {borrowerHref ? (
                <Link className="borrowerHeader-link" to={borrowerHref} onClick={() => track(STATSIG.LOAN.BORROWER_OPEN)}>
                    <div className="borrowerLoanInfoItem borrowerLoanInfoItem--borrower">
                        <div className="borrowerLoanInfoItemTitle">Borrower</div>
                        <div className="borrowerLoanInfoItemValue">{borrowerValueWithTip}</div>
                    </div>
                </Link>
            ) : (
                <div className="borrowerLoanInfoItem borrowerLoanInfoItem--borrower">
                    <div className="borrowerLoanInfoItemTitle">Borrower</div>
                    <div className="borrowerLoanInfoItemValue">{borrowerValueWithTip}</div>
                </div>
            )}
            <div className="borrowerLoanInfoItem">
                <div className="borrowerLoanInfoItemTitle">Purpose</div>
                <div className="borrowerLoanInfoItemValue">{purposeLabel}</div>
            </div>
            {/* The one item that gives way when the bar is narrow, so the bar stays on one line. */}
            <div className="borrowerLoanInfoItem borrowerLoanInfoItem--address">
                <div className="borrowerLoanInfoItemTitle">Address</div>
                <Tooltip title={address} placement="bottom">
                    <div className="borrowerLoanInfoItemValue">{address}</div>
                </Tooltip>
            </div>
            <div className="borrowerLoanInfoItem">
                <div className="borrowerLoanInfoItemTitle">DTI</div>
                <Tooltip title={dtiLabel && dtiLabel !== "Calculate" ? "Housing ratio / Total DTI" : null} placement="bottom">
                    <div className="borrowerLoanInfoItemValue">{dtiValue}</div>
                </Tooltip>
            </div>
            <div className="borrowerLoanInfoItem">
                <div className="borrowerLoanInfoItemTitle">LTV</div>
                <div className="borrowerLoanInfoItemValue">{formatLtv(application)}</div>
            </div>
            <div className="borrowerLoanInfoItem">
                <div className="borrowerLoanInfoItemTitle">Loan Amount</div>
                <div className="borrowerLoanInfoItemValue">{formatLoanAmount(application?.selection?.loansize)}</div>
            </div>
            <div className="borrowerLoanInfoItem">
                <div className="borrowerLoanInfoItemTitle">Rate</div>
                <div className="borrowerLoanInfoItemValue">{rateValue}</div>
            </div>
        </div>
    )
}
